// src/context/AuthContext.tsx

import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
// import { setUserId, identify, Identify, reset } from '@amplitude/analytics-browser'; // Removed: Used AnalyticsManager
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithPhoneNumber, RecaptchaVerifier, ConfirmationResult } from 'firebase/auth';
import { app as firebaseApp, initFirebaseAnalytics } from '../firebaseClient';
// import clevertap from '../clevertapClient'; // Removed: Used AnalyticsManager
import { AnalyticsManager } from '../utils/analytics'; // UPDATED
import { AnalyticsEvents } from '../utils/AnalyticsEvents'; // UPDATED
import { amplitudeApp } from '../index';
import { EnrichedProfile } from '../components/ProfileCard';
import toast from 'react-hot-toast';
import { peekAuthSource, clearAuthSource } from "../utils/authSource";


export type PlanTier = 'free' | 'monthly' | 'yearly';
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due' | 'incomplete' | null;

const getCurrencyFromCountry = (countryCode: string) => {
  const code = countryCode.toUpperCase();
  if (code === 'IN') return 'INR';
  if (code === 'GB') return 'GBP';
  if (code === 'AE') return 'AED';
  return 'USD';
};

interface UserProfile {
  id: string;
  name: string;
  // Added for analytics sync
  preferred_language?: string;
  age_bucket?: string;
  user_birth_details?: any;
}

interface VerifyMsg91OtpPayload {
  accessToken: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  variant: string;
  countryCode?: string;
}

interface PhoneOtpRequest {
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface PhoneOtpVerify extends PhoneOtpRequest {
  otp: string;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isSubscribed: boolean;
  coinBalance: number | null;
  walletBalance: number | null;
  currency: string | null;
  userCountry: string; // [NEW] Expose user country
  // True once the guest geo probe has SETTLED (resolved or failed). userCountry
  // defaults to 'IN', so without this a foreign visitor is indistinguishable
  // from an Indian one for the first few hundred ms — anything that gates on
  // "is this visitor Indian?" must wait for this (see useGuestChat).
  geoChecked: boolean;
  updateWalletBalance: (newBalance: number) => void;
  fetchWalletBalanceFromDB: () => Promise<void>;
  // Coins reserved by an in-progress report (wallet hold). Chat/calls must
  // gate on effectiveBalance = walletBalance - coinsReserved so held coins
  // can't be respent while a report generates.
  coinsReserved: number;
  effectiveBalance: number | null;
  applyHoldOptimistically: (amountMinor: number) => void;
  planTier: PlanTier | null;
  subscriptionStatus: SubscriptionStatus;
  currentPeriodEnd: string | null;
  checkingStatus: boolean;
  userProfiles: UserProfile[] | null;
  signOut: () => void;
  signInWithGoogle: () => void;
  signInWithFirebaseGoogle: () => Promise<void>;
  refreshUserStatus: () => Promise<void>;
  updateCoinBalance: (newBalance: number) => void;
  requestPhoneOtp: (payload: PhoneOtpRequest) => Promise<void>;
  verifyPhoneOtp: (payload: PhoneOtpVerify) => Promise<void>;
  requestFirebasePhoneOtp: (payload: { phone: string; recaptchaVerifier: RecaptchaVerifier }) => Promise<ConfirmationResult>;
  verifyFirebasePhoneOtp: (payload: { confirmationResult: ConfirmationResult; otp: string; firstName?: string; lastName?: string; email?: string }) => Promise<void>;
  verifyMsg91Otp: (payload: VerifyMsg91OtpPayload) => Promise<void>;
  canAddProfile: boolean;
  pricingVariant: string;
  enrichedProfiles: EnrichedProfile[];
  selectedProfile: EnrichedProfile | null;
  setSelectedProfile: (profile: EnrichedProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const navigate = useNavigate();
  // const location = useLocation(); // Unused

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [coinBalance, setCoinBalance] = useState<number | null>(null);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [coinsReserved, setCoinsReserved] = useState<number>(0);
  const [currency, setCurrency] = useState<string | null>(null);
  const [userCountry, setUserCountry] = useState<string>('IN'); // Default to IN
  // Mirror of userCountry for the auth effect below. That effect only reads the
  // country to stamp one analytics property, but listing it as a DEPENDENCY made
  // the effect tear down and re-run every time the country resolved — and the
  // effect itself is what resolves it (detectGuestLocation). One page load then
  // re-ran getSession() + re-subscribed onAuthStateChange, and each pass called
  // fetchUserStatus again, multiplying every query behind it (6x users,
  // 6x chat_history on a marketing page). A ref gives the same value with no
  // dependency, so the subscription is created exactly once.
  const userCountryRef = useRef<string>('IN');
  useEffect(() => { userCountryRef.current = userCountry; }, [userCountry]);
  const [geoChecked, setGeoChecked] = useState<boolean>(false);
  const [planTier, setPlanTier] = useState<PlanTier | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(null);
  const [userProfiles, setUserProfiles] = useState<UserProfile[] | null>(null);
  const [enrichedProfiles, setEnrichedProfiles] = useState<EnrichedProfile[]>([]);
  const [selectedProfile, _setSelectedProfile] = useState<EnrichedProfile | null>(null);

  const setSelectedProfile = (profile: EnrichedProfile | null) => {
    _setSelectedProfile(profile);
    if (profile) {
      localStorage.setItem('selectedProfileId', profile.id);
    } else {
      localStorage.removeItem('selectedProfileId');
    }
  };

  const [pricingVariant, setPricingVariant] = useState<string>('control');
  const MONETIZATION_FLAG_KEY = "pricingexperiment";

  // --- Helper: Fetch Payment Stats ---
  const fetchPaymentStats = async (userId: string) => {
    try {

      const { data: allPayments, error: historyError } = await supabase
        .from('payments')
        .select('amount, created_at')
        .eq('user_id', userId)
        .in('status', ['succeeded', 'completed', 'captured'])
        .order('created_at', { ascending: false });

      if (historyError) throw historyError;

      const payments = allPayments || [];
      const count = payments.length;

      const totalAmount = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

      // Recent Amount (First item due to desc order)
      const recentAmount = payments.length > 0 ? (Number(payments[0].amount) || 0) : 0;
      const lastRechargeDate = payments.length > 0 ? payments[0].created_at : null;

      return {
        total_payments_made: count,
        total_amount_recharged: totalAmount,
        recent_amount_recharged: recentAmount,
        last_recharge_date: lastRechargeDate
      };
    } catch (e) {
      console.warn('[AuthContext] Failed to fetch payment stats', e);
      return {
        total_payments_made: 0,
        total_amount_recharged: 0,
        recent_amount_recharged: 0,
        last_recharge_date: null
      };
    }
  };

  // --- Helper: Fetch Chat Stats ---
  const fetchChatStatsV2 = async (userId: string) => {
    try {
      // 1. INCREASE LIMIT to 50 (filtered by role)
      const { data, error } = await supabase
        .from('chat_history')
        .select('question_category, sub_category')
        .eq('user_id', userId)
        .eq('role', 'user') // Filter at DB level to prevent timeout
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) throw error;

      const userMessages = data || [];

      if (userMessages.length === 0) return { recent_question_category: null, most_asked_question_category: null, recent_question_sub_category: null, most_asked_question_sub_category: null };

      // 2. Get Recent Category (First non-null, non-greeting)
      const recentMsg = userMessages.find(msg => msg.question_category && msg.question_category !== 'greeting');
      const recent_question_category = recentMsg ? recentMsg.question_category : 'general';
      const recent_question_sub_category = recentMsg ? recentMsg.sub_category : null;

      // 3. Calculate Most Asked (Weighted Logic)
      const frequency: Record<string, number> = {};
      const subFrequency: Record<string, number> = {};
      let maxCount = 0;
      let most_asked_question_category = null;
      let maxSubCount = 0;
      let most_asked_question_sub_category = null;

      // First pass: Count everything
      for (const item of userMessages) {
        const cat = item.question_category;
        const subCat = item.sub_category;

        if (cat && cat !== 'greeting' && cat !== 'default') {
          frequency[cat] = (frequency[cat] || 0) + 1;
        }
        if (subCat) {
          subFrequency[subCat] = (subFrequency[subCat] || 0) + 1;
        }
      }

      // 4. FIND WINNER (Prioritize Specific Categories over General)
      // We look for the highest count, IGNORING 'general' first.
      Object.keys(frequency).forEach(cat => {
        if (cat === 'general') return; // Skip general for now

        if (frequency[cat] > maxCount) {
          maxCount = frequency[cat];
          most_asked_question_category = cat;
        }
      });

      // Calculate Most Asked Subcategory
      Object.keys(subFrequency).forEach(sub => {
        if (subFrequency[sub] > maxSubCount) {
          maxSubCount = subFrequency[sub];
          most_asked_question_sub_category = sub;
        }
      });

      // If we found NO specific category (maxCount is still 0), 
      // OR if we want to fallback to general if it exists:
      if (!most_asked_question_category && frequency['general'] > 0) {
        most_asked_question_category = 'general';
      }

      return { recent_question_category, most_asked_question_category, recent_question_sub_category, most_asked_question_sub_category };

    } catch (e) {
      console.warn('[AuthContext] Failed to fetch chat stats', e);
      return { recent_question_category: null, most_asked_question_category: null, recent_question_sub_category: null, most_asked_question_sub_category: null };
    }
  };

  // --- Helper: Fetch Service Costs ---
  const fetchServiceCosts = async (currency: string, variant: string) => {
    try {
      const { data, error } = await supabase
        .from('service_prices')
        .select('service_key, price_amount')
        .eq('currency_code', currency)
        .eq('variant_name', variant)
        .in('service_key', ['chat_message', 'voice_call_minute']); // Fetch both

      if (error) throw error;

      const costs: Record<string, number> = {};
      data?.forEach(item => {
        costs[item.service_key] = item.price_amount;
      });

      return {
        chatCost: costs['chat_message'] || 0, // Default to 0 if missing (handle div by 0 later)
        callCost: costs['voice_call_minute'] || 0
      };
    } catch (e) {
      console.warn('[AuthContext] Failed to fetch service costs', e);
      return { chatCost: 0, callCost: 0 };
    }
  };

  // --- Helper: Fetch Total Usage Stats ---
  const fetchTotalUsageStats = async (userId: string) => {
    try {
      // 1. Total Chats Done
      const { count: chatCount, error: chatError } = await supabase
        .from('chat_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('role', 'user');

      if (chatError) throw chatError;

      // 2. Total Call Minutes Done
      const { data: callData, error: callError } = await supabase
        .from('call_logs')
        .select('duration_seconds')
        .eq('user_id', userId)
        .eq('status', 'completed');

      if (callError) throw callError;

      const totalSeconds = callData?.reduce((sum, log) => sum + (log.duration_seconds || 0), 0) || 0;
      const totalMinutes = Math.floor(totalSeconds / 60);

      return {
        total_chats_done: chatCount || 0,
        total_call_minutes_done: totalMinutes
      };
    } catch (e) {
      console.warn('[AuthContext] Failed to fetch total usage stats', e);
      return { total_chats_done: 0, total_call_minutes_done: 0 };
    }
  };

  // --- Helper: Sync User Properties ---
  const syncUserProperties = async (currentUser: User, userData: any, profilesData: UserProfile[]) => {
    try {
      const phoneNumber = userData?.phone_number || userData?.msg91_phone_number || currentUser.phone;
      const walletBalance = userData?.wallet_balance ?? 0;
      const currency = userData?.currency_code ?? 'USD';
      const variant = userData?.pricing_variant || 'control';

      const [paymentStats, chatStats, serviceCosts, usageStats] = await Promise.all([
        fetchPaymentStats(currentUser.id),
        fetchChatStatsV2(currentUser.id),
        fetchServiceCosts(currency, variant),
        fetchTotalUsageStats(currentUser.id)
      ]);

      // Calculate Remaining Usage
      // If cost is 0 or less, avoid division by zero or negative infinity. 
      // If cost is 0 (free?), technically infinite usage, but let's set a safe max or 0 if balance is 0?
      // For now, if cost <= 0, we can't calculate "left", so maybe set to -1 or 0.

      let chatsLeft = 0;
      let callsLeft = 0;

      if (serviceCosts.chatCost > 0) {
        chatsLeft = Math.floor(walletBalance / serviceCosts.chatCost);
      }
      if (serviceCosts.callCost > 0) {
        callsLeft = Math.floor(walletBalance / serviceCosts.callCost);
      }

      const properties: any = {
        Identity: currentUser.id,
        Name: profilesData?.[0]?.name || 'Unknown', // Use first profile name
        Email: currentUser.email ?? 'unknown',
        Phone: phoneNumber,
        source: 'web_app', // Added source property

        // Strict Mobile App Sync (snake_case)
        wallet_balance: walletBalance,
        currency: currency,

        'Total Profiles': profilesData?.length ?? 0,

        // Communication Preferences (Defaulting to true as DB columns might not be selected)
        'MSG-email': true,
        'MSG-push': true,
        'MSG-sms': true,
        'MSG-whatsapp': true,

        // Advanced Properties
        total_payments_made: paymentStats.total_payments_made,
        'Total Amount Recharged': paymentStats.total_amount_recharged,
        'Recent Amount Recharged': paymentStats.recent_amount_recharged,
        last_recharge_date: paymentStats.last_recharge_date,

        // Usage Stats (Remaining)
        no_of_chats_left: chatsLeft,
        call_minutes_left: callsLeft,

        // Usage History (Total Done)
        total_chats_done: usageStats.total_chats_done,
        total_call_minutes_done: usageStats.total_call_minutes_done,

        recent_question_category: chatStats.recent_question_category,
        most_asked_question_category: chatStats.most_asked_question_category,
        recent_question_sub_category: chatStats.recent_question_sub_category,
        most_asked_question_sub_category: chatStats.most_asked_question_sub_category,
      };

      // Add profile details from primary profile if available
      if (profilesData && profilesData.length > 0) {
        const primaryProfile = profilesData[0];
        properties['Language'] = primaryProfile.preferred_language || 'en';

        // Access nested join data if available (depends on query)
        if (primaryProfile.user_birth_details) {
          const details = Array.isArray(primaryProfile.user_birth_details)
            ? primaryProfile.user_birth_details[0]
            : primaryProfile.user_birth_details;

          if (details) {
            if (details.gender) properties['Gender'] = details.gender;
            if (details.date_of_birth) {
              try {
                const dob = new Date(details.date_of_birth);
                properties['DOB'] = dob;

                // --- Calculate Age Bucket ---
                const today = new Date();
                let age = today.getFullYear() - dob.getFullYear();
                const m = today.getMonth() - dob.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                  age--;
                }

                let bucket = '65+';
                if (age <= 17) bucket = '0-17';
                else if (age >= 18 && age <= 24) bucket = '18-24';
                else if (age >= 25 && age <= 34) bucket = '25-34';
                else if (age >= 35 && age <= 44) bucket = '35-44';
                else if (age >= 45 && age <= 54) bucket = '45-54';
                else if (age >= 55 && age <= 64) bucket = '55-64';

                properties['age_bucket'] = bucket;
                properties['Age'] = age; // Sync actual age as well

                // --- Sync Age Bucket to DB (if missing or different) ---
                // We check if the fetched profile data already has the correct bucket.
                // Note: userProfiles query in fetchUserStatus needs to select 'age_bucket' to verify.
                // If we didn't fetch it, we can just upsert or update blindly, but checking is better.
                // For now, let's fire an update if we calculated it.
                supabase
                  .from('user_profiles')
                  .update({ age_bucket: bucket })
                  .eq('id', primaryProfile.id)
                  .then(({ error }) => {
                    if (error) console.warn('[AuthContext] Failed to update age_bucket', error);
                  });

              } catch (e) { /* ignore */ }
            }
            if (details.time_of_birth) properties['Time of Birth'] = details.time_of_birth;
            if (details.birth_place) properties['Place of Birth'] = details.birth_place;
          }
        }
      }

      // --- Calculate Preferred Service (Chat vs Voice) ---
      // Logic: Compare estimated total spend
      const estimatedChatSpend = (usageStats.total_chats_done || 0) * (serviceCosts.chatCost || 0);
      const estimatedCallSpend = (usageStats.total_call_minutes_done || 0) * (serviceCosts.callCost || 0);

      let preferredService = 'Neutral';
      if (estimatedChatSpend > estimatedCallSpend) {
        preferredService = 'Chat';
      } else if (estimatedCallSpend > estimatedChatSpend) {
        preferredService = 'Voice Call';
      } else if (estimatedChatSpend > 0 && estimatedChatSpend === estimatedCallSpend) {
        preferredService = 'Mixed'; // Equal non-zero spend
      } else if (estimatedChatSpend === 0 && estimatedCallSpend === 0) {
        // If no usage, maybe check wallet history? optimize later.
        preferredService = 'None';
      }

      properties['preferred_service'] = preferredService;

      AnalyticsManager.setUserProperties(properties);

      // Attempt Astro Sync (Background)
      syncAstroProperties(profilesData);

    } catch (e) {
      console.error('[AuthContext] Error syncing user properties:', e);
    }
  };

  // --- Helper: Sync Astro Properties ---
  const syncAstroProperties = async (profiles: UserProfile[]) => {
    if (!profiles || profiles.length === 0) return;
    const profileId = profiles[0].id; // Use primary profile

    // Check last sync time to avoid spamming
    const lastSyncKey = `last_astro_sync_v4_${profileId}`; // Bump version
    const lastSyncStr = localStorage.getItem(lastSyncKey);
    if (lastSyncStr) {
      const lastSyncDate = new Date(lastSyncStr);
      const diffHours = (new Date().getTime() - lastSyncDate.getTime()) / (1000 * 60 * 60);
      if (diffHours < 24) return; // Skip if synced in last 24h
    }

    try {
      const { data, error } = await supabase.functions.invoke('TEST_generate-astro-data', {
        body: { profile_id: profileId, scope: 'charts' }
      });

      if (error || !data) return;

      const tables = data.processedTables; // Assuming same structure as Flutter response
      const dashaList = tables?.vimshottariDasha;
      const planets = tables?.d1Planets;

      const properties: any = {};
      const now = new Date();

      // Dasha Logic
      if (dashaList && Array.isArray(dashaList)) {
        const current = dashaList.find((p: any) => {
          const start = new Date(p.startDateTime);
          const end = new Date(p.endDateTime);
          return now >= start && now <= end;
        });

        if (current) {
          properties['current_mahadasha'] = current.mahadashaLord;
          properties['current_antardasha'] = current.antardashaLord;

          // Lookup Houses for Lords
          if (planets && Array.isArray(planets)) {
            try {
              const mdLordName = current.mahadashaLord.trim().toLowerCase();
              const adLordName = current.antardashaLord.trim().toLowerCase();

              const mdPlanet = planets.find((p: any) => p.planet.trim().toLowerCase() === mdLordName);
              if (mdPlanet && mdPlanet.house) {
                properties['current_mahadasha_lord_house'] = mdPlanet.house.toString();
              }

              const adPlanet = planets.find((p: any) => p.planet.trim().toLowerCase() === adLordName);
              if (adPlanet && adPlanet.house) {
                properties['current_antardasha_lord_house'] = adPlanet.house.toString();
              }
            } catch (e) { /* ignore */ }
          }
        }
      }

      // Planet Logic
      if (planets && Array.isArray(planets)) {
        const moon = planets.find((p: any) => p.planet.trim().toLowerCase().includes('moon'));
        if (moon) {
          properties['birth_moon_sign'] = moon.sign;
          properties['birth_nakshatra'] = moon.nakshatra;
        }
        const sun = planets.find((p: any) => p.planet.trim().toLowerCase().includes('sun'));
        if (sun) {
          properties['birth_sun_sign'] = sun.sign;
        }
        const asc = planets.find((p: any) => p.planet.trim().toLowerCase().includes('asc') || p.planet.trim().toLowerCase().includes('lagna'));
        if (asc) {
          properties['birth_ascendant_sign'] = asc.sign;
        }
      }

      if (Object.keys(properties).length > 0) {
        AnalyticsManager.setUserProperties(properties);
        localStorage.setItem(lastSyncKey, new Date().toISOString());
      }
    } catch (e) {
      console.warn('[AuthContext] Astro sync failed', e);
    }
  };


  // Collapses overlapping refreshes. getSession() and the INITIAL_SESSION event
  // from onAuthStateChange both fire on a normal load, and the visibilitychange
  // handler can land on top of either — each one previously ran the full query
  // fan-out. Keyed by user id (or 'anon') so a real account switch is never
  // swallowed; concurrent callers for the SAME user share one promise.
  const inFlightRef = useRef<{ key: string; p: Promise<void> } | null>(null);

  const fetchUserStatusInner = useCallback(async (currentSession: Session | null) => {
    const currentUser = currentSession?.user ?? null;
    setSession(currentSession);
    setUser(currentUser);

    if (currentUser) {
      setCheckingStatus(true);
      AnalyticsManager.setUserId(currentUser.id); // UPDATED

      // --- START: REVISED AND FINAL LOGIC ---

      const variantFromCreation = sessionStorage.getItem('variantAfterCreation');
      if (variantFromCreation) {
        console.log(`[AuthContext] Using locally trusted variant immediately after creation: ${variantFromCreation}`);
        setPricingVariant(variantFromCreation);
        sessionStorage.removeItem('variantAfterCreation');
      }

      // Now, fetch all other user data from the database.
      // UPDATED: Added preferred_language to user_profiles selection
      const [userDataRes, profilesDataRes] = await Promise.all([
        supabase.from('users')
          .select(`coin_balance, wallet_balance, coins_reserved, currency_code, plan_tier, subscription_status, subscription_end_date, is_admin, phone_number, msg91_phone_number, email, pricing_variant`)
          .eq('id', currentUser.id)
          .single(),
        supabase.from('user_profiles')
          .select('id, name, age_bucket, preferred_language, user_birth_details(gender, date_of_birth, time_of_birth, birth_place)') // Added details for sync
          .eq('user_id', currentUser.id)
          // Hide soft-deleted profiles. is_deleted is nullable — NULL or false
          // both mean "active"; a bare .eq(false) would silently strip legacy
          // NULL rows. Mirrors the app's filter.
          .or('is_deleted.eq.false,is_deleted.is.null')
          .order('created_at', { ascending: true }) // Ensure order
      ]);

      let { data: userData } = userDataRes;
      const { data: profilesData } = profilesDataRes;

      // EVERY branch below is gated on `userData`, so if that select came back
      // empty the account silently ends up with no currency and no wallet, can
      // never be billed, and every chat it sends fails to generate. Two ways it
      // comes back empty, both real:
      //
      //  1. A brand-new signup outruns its own row — the session is live before
      //     handle_new_user_setup's INSERT is visible. Retry; it shows up.
      //  2. The row genuinely does not exist and never will. That trigger fires
      //     only on INSERT into auth.users, so an identity whose row was removed
      //     (or that predates the trigger) never gets one back. Recreate it,
      //     exactly as the trigger does — RLS policy "Users can insert own data"
      //     (auth.uid() = id) permits precisely this.
      const USER_COLS = `coin_balance, wallet_balance, coins_reserved, currency_code, plan_tier, subscription_status, subscription_end_date, is_admin, phone_number, msg91_phone_number, email, pricing_variant`;

      for (let attempt = 0; !userData && attempt < 3; attempt++) {
        await new Promise((r) => setTimeout(r, 300));
        const { data: retried } = await supabase.from('users')
          .select(USER_COLS).eq('id', currentUser.id).maybeSingle();
        userData = retried;
      }

      if (!userData) {
        console.warn('[AuthContext] no public.users row for', currentUser.id, '— recreating it');
        await supabase.from('users').insert({ id: currentUser.id, email: currentUser.email ?? null });
        const { data: healed } = await supabase.from('users')
          .select(USER_COLS).eq('id', currentUser.id).maybeSingle();
        userData = healed; // if the insert lost a race, the re-read still finds it
      }

      if (!variantFromCreation) {
        // This block now ONLY runs if the user was NOT just created.
        if (userData?.pricing_variant) {
          console.log(`[AuthContext] Using stored variant from DB: ${userData.pricing_variant}`);
          setPricingVariant(userData.pricing_variant);
        } else if (userData) {
          console.log(`[AuthContext] Backfilling variant for existing user ${currentUser.id}...`);
          await amplitudeApp.experiment.fetch();
          const expVariant = amplitudeApp.experiment.variant(MONETIZATION_FLAG_KEY);
          const variantToSave = expVariant?.value || 'control';
          setPricingVariant(variantToSave);

          // Asynchronously save to DB
          supabase
            .from('users')
            .update({ pricing_variant: variantToSave })
            .eq('id', currentUser.id)
            .then(({ error }) => {
              if (error) {
                console.error('[AuthContext] CRITICAL: Error saving backfilled variant to DB:', error);
              } else {
                console.log(`[AuthContext] Successfully saved backfilled variant '${variantToSave}' for user.`);
              }
            });
        }
      }
      if (userData && !userData.currency_code) {
        console.log('User has no currency. Initializing wallet for OAuth user...');
        try {
          await amplitudeApp.experiment.fetch();
          const expVariant = amplitudeApp.experiment.variant(MONETIZATION_FLAG_KEY);
          const variantName = expVariant?.value || 'control';

          console.log(`[AuthContext] Creating user with variant: ${variantName}.`);

          const { data: initData, error: initError } = await supabase.functions.invoke(
            'init-user-wallet',
            { body: { variant_name: variantName } }
          );

          if (!initError && initData?.success) {
            setCurrency(initData.currency);
            setWalletBalance(initData.balance);
            if (userData) {
              userData.currency_code = initData.currency;
              userData.wallet_balance = initData.balance;
            }
          } else {
            console.error("Failed to initialize user wallet:", initError);
            setCurrency('USD'); setWalletBalance(0);
          }
        } catch (e) {
          console.error("Error during wallet initialization with variant:", e);
          setCurrency('USD'); setWalletBalance(0);
        }
      } else if (userData) {
        setCurrency(userData.currency_code);
        setWalletBalance(userData.wallet_balance || 0);
      }
      // Sync reserved coins (held by an in-progress report) on load.
      if (userData) setCoinsReserved(userData.coins_reserved ?? 0);

      // --- UPDATED: Analytics Sync ---
      await syncUserProperties(currentUser, userData, profilesData || []);

      setIsSubscribed(userData?.subscription_status === 'active');
      const email = (currentUser.email || '').toLowerCase();
      setIsAdmin(Boolean(userData?.is_admin) || ['shubhamkush012@gmail.com', 'vikram@stratnova.ai'].includes(email));
      setCoinBalance(userData?.coin_balance ?? 10);
      setPlanTier(userData?.plan_tier ?? 'free');
      setSubscriptionStatus(userData?.subscription_status ?? null);
      setCurrentPeriodEnd(userData?.subscription_end_date ?? null);
      setSubscriptionStatus(userData?.subscription_status ?? null);
      setCurrentPeriodEnd(userData?.subscription_end_date ?? null);
      if (profilesData) {
        setUserProfiles(profilesData);

        const enriched = profilesData.map((profile: any) => {
          const birthDetails = Array.isArray(profile.user_birth_details)
            ? profile.user_birth_details[0]
            : profile.user_birth_details;
          return {
            id: profile.id,
            name: profile.name,
            preferred_language: profile.preferred_language,
            gender: birthDetails?.gender || "N/A",
            date_of_birth: birthDetails?.date_of_birth || "",
            time_of_birth: birthDetails?.time_of_birth || "",
            birth_place: birthDetails?.birth_place || "N/A",
          };
        });
        setEnrichedProfiles(enriched);

        // Initialize Selected Profile
        const savedProfileId = localStorage.getItem('selectedProfileId');
        let initialProfile = null;
        if (savedProfileId) {
          initialProfile = enriched.find((p: EnrichedProfile) => p.id === savedProfileId);
        }
        if (!initialProfile && enriched.length > 0) {
          initialProfile = enriched[0];
        }

        if (initialProfile) {
          setSelectedProfile(initialProfile); // Changed _setSelectedProfile to setSelectedProfile
          // Ensure it's saved in case it was a fallback
          if (!savedProfileId) {
            localStorage.setItem('selectedProfileId', initialProfile.id);
          }
        }
      }



      setCheckingStatus(false);
    } else {
      AnalyticsManager.setUserId(null); // UPDATED: Reset ID
      // reset(); // handled by AnalyticsManager
      setIsSubscribed(false);
      setIsAdmin(false);
      setCoinBalance(null);
      setWalletBalance(null);
      setCoinsReserved(0);
      setCurrency(null);
      setPlanTier(null);
      setSubscriptionStatus(null);
      setCurrentPeriodEnd(null);
      setUserProfiles(null);
      setPricingVariant('control');
      setEnrichedProfiles([]);
      setSelectedProfile(null);
      setCheckingStatus(false);
    }
  }, []); // syncUserProperties defined inside but depends on outer scope or should be defined outside, but inside component is fine.

  const fetchUserStatus = useCallback(async (currentSession: Session | null) => {
    const key = currentSession?.user?.id ?? 'anon';
    const current = inFlightRef.current;
    if (current && current.key === key) return current.p;   // already running for this user
    const p = fetchUserStatusInner(currentSession).finally(() => {
      if (inFlightRef.current?.p === p) inFlightRef.current = null;
    });
    inFlightRef.current = { key, p };
    return p;
  }, [fetchUserStatusInner]);

  const detectGuestLocation = async () => {
    try {
      // QA override — lets us preview the foreign-visitor experience (guest chat,
      // Google login, USD pricing) from India without a VPN. Guests only; it is
      // never read for a signed-in user, and it can't change what anyone is
      // billed (currency is resolved server-side at signup).
      //   localStorage.setItem('auraForceCountry', 'US')  → reload
      //   localStorage.removeItem('auraForceCountry')     → back to real geo
      const forcedCountry = localStorage.getItem('auraForceCountry');
      if (forcedCountry) {
        console.warn(`[AuthContext] auraForceCountry override active: ${forcedCountry}`);
        setUserCountry(forcedCountry.toUpperCase());
        setCurrency(getCurrencyFromCountry(forcedCountry));
        return;
      }

      const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
      if (apiKey) {
        const response = await fetch(`https://api.geoapify.com/v1/ipinfo?apiKey=${apiKey}`);
        if (response.ok) {
          const geoData = await response.json();
          const countryCode = geoData.country?.iso_code || 'US';
          setUserCountry(countryCode);
          const detectedCurrency = getCurrencyFromCountry(countryCode);
          setCurrency(detectedCurrency);
        }
      }
    } catch (error) {
      console.error('Guest location detection failed, defaulting to USD', error);
      setCurrency('USD');
    } finally {
      // Settled either way (including "no API key" / probe failed, which leave
      // userCountry at its 'IN' default). Callers waiting on the answer must not
      // hang forever on a failure.
      setGeoChecked(true);
    }
  };

  // --- EFFECT #1: Handles initial session loading and auth state changes ---
  useEffect(() => {
    // Failsafe for geoChecked: the guest geo probe runs off the auth callback
    // below. If that never fires (or the request hangs), anything waiting on the
    // country would wait forever — so declare it settled after 3s. userCountry
    // is still its 'IN' default then, i.e. we fail closed to the Indian path.
    const geoFailsafe = window.setTimeout(() => setGeoChecked(true), 3000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      fetchUserStatus(session);
    }).finally(() => setLoading(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_OUT') {
        // Tell a genuine session expiry (token/refresh failed) apart from a
        // deliberate logout: signOut() clears loginEventTracked *before* calling
        // supabase.auth.signOut(), so if the flag is still set here, the session
        // ended on its own — surface it instead of the user silently landing on
        // a redirect with no explanation.
        if (sessionStorage.getItem('loginEventTracked')) {
          sessionStorage.removeItem('loginEventTracked');
          toast.error('Your session has expired. Please sign in again.');
        }
      }

      if (_event === 'SIGNED_IN') {
        // The pending acquisition source has served its purpose by now: the OTP
        // verify already carried it. Cleared HERE rather than in the verify call
        // because Google sign-in never goes through that call at all, and a
        // source left behind would be attached to whatever the visitor logs in
        // with next — crediting the compatibility calculator for an unrelated
        // signup days later.
        clearAuthSource();

        const isOAuthReturn = window.location.hash.includes('access_token');
        if (isOAuthReturn) {
          sessionStorage.setItem('justLoggedIn', 'true');
        }

        if (!sessionStorage.getItem('loginEventTracked')) {
          const loginMethod = session?.user?.app_metadata?.provider || (session?.user?.email ? 'email' : 'unknown');
          // UPDATED: Analytics Event
          AnalyticsManager.trackEvent(AnalyticsEvents.LoginSuccess, {
            login_method: loginMethod,
            country_code: userCountryRef.current
          });
          sessionStorage.setItem('loginEventTracked', 'true');
        }
      }

      fetchUserStatus(session);

      if (!session) {
        detectGuestLocation();
      } else {
        // Signed in: no guest geo probe runs, so nothing else would ever settle
        // this. (The country gate only ever applies to logged-out visitors.)
        setGeoChecked(true);
      }
    });

    return () => {
      window.clearTimeout(geoFailsafe);
      subscription?.unsubscribe();
    };
    // NOTE: userCountry deliberately NOT a dependency — see userCountryRef above.
  }, [fetchUserStatus]);

  // --- EFFECT #2: Handles the one-time redirect after a user logs in ---
  useEffect(() => {
    if (!checkingStatus && user && userProfiles) {
      const justLoggedIn = sessionStorage.getItem('justLoggedIn');

      if (justLoggedIn === 'true') {
        sessionStorage.removeItem('justLoggedIn');

        if (userProfiles.length === 0) {
          navigate('/profiles/new');
        } else {
          navigate('/chat');
        }
      }
    }
  }, [checkingStatus, user, userProfiles, navigate]);

  // --- EFFECT #3: Refreshes user status when tab becomes visible ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          fetchUserStatus(session);
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchUserStatus]);


  const signInWithGoogle = async () => {
    // UPDATED: Analytics Event
    AnalyticsManager.trackEvent(AnalyticsEvents.GoogleLoginStarted, { method: 'supabase_oauth' });
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  };

  const signInWithFirebaseGoogle = async () => {
    // UPDATED: Analytics Event
    AnalyticsManager.trackEvent(AnalyticsEvents.GoogleLoginStarted, { method: 'firebase_popup' });
    try {
      await initFirebaseAnalytics();
      const auth = getAuth(firebaseApp);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const firebaseToken = await result.user.getIdToken();
      const { data, error } = await supabase.functions.invoke('firebase-auth-exchange-2', { body: { firebaseToken } });

      if (error) throw new Error('Failed to link Firebase account.');

      if (data?.session?.access_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        if (sessionError) throw sessionError;

        sessionStorage.setItem('justLoggedIn', 'true');
        const { data: sessionData } = await supabase.auth.getSession();
        await fetchUserStatus(sessionData.session);

      } else if (data?.action_link) {
        window.location.href = data.action_link;
      } else {
        throw new Error('Firebase exchange returned no usable session data.');
      }
    } catch (e: any) {
      console.error('Firebase sign-in failed:', e);
      alert(`Firebase login failed: ${e.message}`);
    }
  };

  const requestPhoneOtp = async (payload: PhoneOtpRequest) => {
    try {
      const { phone, firstName, lastName, email } = payload;
      const sanitizedPhone = phone.trim();
      if (!sanitizedPhone.startsWith('+')) throw new Error('Phone number must include country code.');

      // UPDATED: Analytics
      AnalyticsManager.trackEvent(AnalyticsEvents.OtpSent, { phone: sanitizedPhone, method: 'supabase' });

      const userData: Record<string, string> = {};
      if (firstName) userData.first_name = firstName.trim();
      if (lastName) userData.last_name = lastName.trim();
      if (email) userData.email = email.trim();

      const { error } = await supabase.auth.signInWithOtp({
        phone: sanitizedPhone,
        options: { data: Object.keys(userData).length > 0 ? userData : undefined },
      });
      if (error) {
        AnalyticsManager.trackEvent(AnalyticsEvents.OtpSendFailed, { phone: sanitizedPhone, error: error.message });
        throw error;
      }
    } catch (err: any) {
      AnalyticsManager.trackEvent(AnalyticsEvents.OtpSendFailed, { phone: payload.phone, error: err.message });
      throw new Error(err?.message || 'Failed to send OTP.');
    }
  };

  const verifyPhoneOtp = async (payload: PhoneOtpVerify) => {
    try {
      const { phone, otp, firstName, lastName, email } = payload;
      if (!phone.trim() || !otp.trim()) throw new Error('Phone and OTP are required.');

      const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
        phone: phone.trim(),
        token: otp.trim(),
        type: 'sms',
      });
      if (verifyError) {
        AnalyticsManager.trackEvent(AnalyticsEvents.LoginFailed, { phone: phone, error: verifyError.message, method: 'supabase' });
        throw verifyError;
      }

      if (verifyData?.session) {
        // Analytics handled in useEffect on state change or here? 
        // Best to handle here for immediate success but effect handles general login

        const userMetadataUpdate: Record<string, string> = {};
        if (firstName?.trim()) userMetadataUpdate.first_name = firstName.trim();
        if (lastName?.trim()) userMetadataUpdate.last_name = lastName.trim();
        if (email?.trim()) userMetadataUpdate.email = email.trim();
        if (Object.keys(userMetadataUpdate).length > 0) {
          await supabase.auth.updateUser({ data: userMetadataUpdate });
        }

        sessionStorage.setItem('justLoggedIn', 'true');
        await fetchUserStatus(verifyData.session);
      }
    } catch (err: any) {
      throw new Error(err?.message || 'Failed to verify OTP.');
    }
  };

  const requestFirebasePhoneOtp = async (payload: { phone: string; recaptchaVerifier: RecaptchaVerifier }): Promise<ConfirmationResult> => {
    try {
      const { phone, recaptchaVerifier } = payload;
      if (!phone.trim().startsWith('+')) throw new Error('Phone number must include country code.');

      AnalyticsManager.trackEvent(AnalyticsEvents.OtpSent, { phone: phone, method: 'firebase' });

      const auth = getAuth(firebaseApp);
      return await signInWithPhoneNumber(auth, phone.trim(), recaptchaVerifier);
    } catch (err: any) {
      AnalyticsManager.trackEvent(AnalyticsEvents.OtpSendFailed, { phone: payload.phone, error: err.message, method: 'firebase' });
      throw new Error(err?.message || 'Failed to send OTP.');
    }
  };

  const verifyFirebasePhoneOtp = async (payload: { confirmationResult: ConfirmationResult; otp: string; firstName?: string; lastName?: string; email?: string }): Promise<void> => {
    try {
      const { confirmationResult, otp, firstName, lastName, email } = payload;
      if (!otp.trim()) throw new Error('OTP is required.');

      const result = await confirmationResult.confirm(otp.trim());
      if (!result.user) throw new Error('Firebase authentication failed.');

      const idToken = await result.user.getIdToken();
      const { data, error } = await supabase.functions.invoke('firebase-auth-exchange-2', { body: { firebaseToken: idToken } });
      if (error) throw error;

      if (data?.action_link) {
        const url = new URL(data.action_link);
        const tokenHash = url.searchParams.get('token_hash') || data.token_hash;
        if (tokenHash) {
          const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: data.type || 'recovery' });
          if (verifyError) throw verifyError;
          if (verifyData?.session) {
            const userMetadataUpdate: Record<string, string> = {};
            if (firstName?.trim()) userMetadataUpdate.first_name = firstName.trim();
            if (lastName?.trim()) userMetadataUpdate.last_name = lastName.trim();
            if (email?.trim()) userMetadataUpdate.email = email.trim();
            if (Object.keys(userMetadataUpdate).length > 0) {
              await supabase.auth.updateUser({ data: userMetadataUpdate });
            }
            sessionStorage.setItem('justLoggedIn', 'true');
            await fetchUserStatus(verifyData.session);
          }
        } else {
          throw new Error('No token hash received from Firebase exchange');
        }
      } else {
        throw new Error('No action link received from Firebase exchange');
      }
    } catch (err: any) {
      AnalyticsManager.trackEvent(AnalyticsEvents.LoginFailed, { error: err.message, method: 'firebase' });
      throw new Error(err?.message || 'Failed to verify OTP.');
    }
  };

  const verifyMsg91Otp = async (payload: VerifyMsg91OtpPayload & { countryCode?: string }) => {
    try {
      const { accessToken, phone, firstName, lastName, email, variant, countryCode } = payload;
      if (!accessToken) throw new Error('Access token is required.');

      const finalVariant = variant || sessionStorage.getItem('guestVariant') || 'control';
      console.log(`[AuthContext-MSG91] User assigned to variant: ${finalVariant}. Verifying token.`);

      sessionStorage.setItem('variantAfterCreation', finalVariant);

      const normalizedPhone = phone.startsWith('+') ? phone.substring(1) : phone;

      // [DEBUG] Log payload to debug international verification failures
      console.log('[AuthContext-MSG91] Verifying with payload:', {
        phone: normalizedPhone,
        accessTokenLength: accessToken?.length,
        variant: finalVariant,
        countryCode: countryCode
      });

      const { data, error } = await supabase.functions.invoke('TEST_msg91_verify_token', {
        body: {
          access_token: accessToken,
          phone: normalizedPhone,
          firstName: firstName?.trim(),
          lastName: lastName?.trim(),
          email: email?.trim(),
          variant_name: finalVariant,
          country_code: countryCode, // Pass country code
          // Which feature sent them here. The backend stamps it as the new
          // user's acquisition `source`, the same marker the public API
          // calculators already use, so marketing can pull the phone list
          // without any new table. Undefined for an ordinary login.
          source: peekAuthSource() || undefined,
        },
      });
      if (error) {
        AnalyticsManager.trackEvent(AnalyticsEvents.LoginFailed, { phone: normalizedPhone, error: error.message, method: 'msg91' });
        throw error;
      }

      let tokenHash = data?.token_hash;
      if (!tokenHash && data?.action_link) {
        try {
          const url = new URL(data.action_link);
          tokenHash = url.searchParams.get('token_hash') || url.searchParams.get('token');
          if (!tokenHash && url.hash) {
            const hashParams = new URLSearchParams(url.hash.substring(1));
            tokenHash = hashParams.get('token_hash') || hashParams.get('token');
          }
        } catch (urlError) {
          console.error('[AuthContext-MSG91] Error parsing action_link:', urlError);
        }
      }

      if (tokenHash) {
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: data.type || 'magiclink' });
        if (!verifyError && verifyData?.session) {

          // Analytics handled in generic useEffect or success flow

          const userMetadataUpdate: Record<string, string> = {};
          if (firstName?.trim()) userMetadataUpdate.first_name = firstName.trim();
          if (lastName?.trim()) userMetadataUpdate.last_name = lastName.trim();
          if (email?.trim()) userMetadataUpdate.email = email.trim();
          if (Object.keys(userMetadataUpdate).length > 0) {
            await supabase.auth.updateUser({ data: userMetadataUpdate });
          }
          sessionStorage.setItem('justLoggedIn', 'true');
          await fetchUserStatus(verifyData.session);
          return;
        } else {
          AnalyticsManager.trackEvent(AnalyticsEvents.LoginFailed, { phone: normalizedPhone, error: verifyError?.message, method: 'msg91' });
        }
      }

      if (data?.action_link) {
        console.error('[AuthContext-MSG91] Token extraction failed, action_link available but not redirecting to preserve localhost');
      }

      throw new Error('Verification failed: Could not extract authentication token.');
    } catch (err: any) {
      throw new Error(err?.message || 'Failed to verify OTP.');
    }
  };

  const signOut = async () => {
    // UPDATED: Analytics Event
    AnalyticsManager.trackEvent(AnalyticsEvents.UserLoggedOut);
    sessionStorage.removeItem('loginEventTracked');
    sessionStorage.removeItem('justLoggedIn');
    await supabase.auth.signOut();
    navigate('/');
  };

  const updateWalletBalance = useCallback((newBalance: number) => setWalletBalance(newBalance), []);
  const updateCoinBalance = useCallback((newBalance: number) => setCoinBalance(newBalance), []);

  // Optimistically reserve coins the moment a report hold is placed, so the
  // chat/call affordability checks (which use effectiveBalance) immediately see
  // the held coins as unavailable — without waiting for the next DB refresh.
  const applyHoldOptimistically = useCallback((amountMinor: number) => {
    setCoinsReserved((prev) => Math.max(0, prev + amountMinor));
  }, []);

  const fetchWalletBalanceFromDB = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('wallet_balance, coins_reserved')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching wallet balance:', error);
        return;
      }

      if (data) {
        setWalletBalance(data.wallet_balance ?? 0);
        // Authoritative reserved value from DB — replaces any optimistic hold.
        setCoinsReserved(data.coins_reserved ?? 0);
        console.log('✅ Wallet refreshed from DB:', data.wallet_balance, 'reserved:', data.coins_reserved ?? 0);

        // Optionally sync latest balance to analytics
        AnalyticsManager.setUserProperties({ wallet_balance: data.wallet_balance ?? 0 });
      }
    } catch (err) {
      console.error('Error in fetchWalletBalanceFromDB:', err);
    }
  }, [user?.id]);

  // Profiles are unlimited for everyone — no subscription required to add more.
  const canAddProfile = true;

  const value = {
    session, user, isAdmin, isSubscribed, coinBalance, planTier,
    subscriptionStatus, currentPeriodEnd, checkingStatus, walletBalance,
    currency, updateWalletBalance, fetchWalletBalanceFromDB,
    coinsReserved,
    effectiveBalance: walletBalance === null ? null : Math.max(0, walletBalance - coinsReserved),
    applyHoldOptimistically,
    userProfiles, signOut, signInWithGoogle,
    signInWithFirebaseGoogle, refreshUserStatus: () => supabase.auth.getSession().then(({ data }) => fetchUserStatus(data.session)),
    updateCoinBalance, requestPhoneOtp, verifyPhoneOtp, requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp, verifyMsg91Otp, canAddProfile, pricingVariant, userCountry, geoChecked,
    enrichedProfiles, selectedProfile,
    setSelectedProfile: (p: EnrichedProfile | null) => {
      _setSelectedProfile(p);
      if (p) {
        localStorage.setItem("selectedProfileId", p.id);
      } else {
        localStorage.removeItem("selectedProfileId");
      }
    }
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
